import { useTranslation } from "react-i18next";
import { Grid, Typography } from "@mui/material";
import { AmountInput } from "./InputAmount";
import { FormProvider, useFormContext } from "react-hook-form";
import { common, input } from "../../../../../../i18n";
import { CustomAutoComplete } from "../../../../../../components/input/CustomAutoComplete";
import { useEffect, useState } from "react";
import { MenuData } from "../../../../../../components/input/CustomSelect";
import { AddEmissionContext } from "../../../../../../contexts/AddEmissionContext";
import NumericField from "../../../../../../components/input/NumericField";

type PaperGrammageGrouped = {
  size: string;
  grammages: {
    grammage: number;
    weightPerReam_g: number;
  }[];
};

const paperGrammagesGrouped: PaperGrammageGrouped[] = [
  {
    size: "A4",
    grammages: [
      { grammage: 70, weightPerReam_g: 2182.95 },
      { grammage: 80, weightPerReam_g: 2494.8 },
      { grammage: 100, weightPerReam_g: 3118.5 },
      { grammage: 120, weightPerReam_g: 3742.2 },
      { grammage: 130, weightPerReam_g: 4054.05 },
      { grammage: 150, weightPerReam_g: 4677.75 },
      { grammage: 160, weightPerReam_g: 4989.6 },
      { grammage: 180, weightPerReam_g: 5613.3 },
      { grammage: 200, weightPerReam_g: 6237.0 },
      { grammage: 210, weightPerReam_g: 6548.85 },
      { grammage: 230, weightPerReam_g: 7172.55 },
      { grammage: 260, weightPerReam_g: 8108.1 },
    ],
  },
  {
    size: "A3",
    grammages: [
      { grammage: 70, weightPerReam_g: 4365.9 },
      { grammage: 80, weightPerReam_g: 4989.6 },
    ],
  },
  {
    size: "A5",
    grammages: [
      { grammage: 70, weightPerReam_g: 1087.8 },
      { grammage: 80, weightPerReam_g: 1243.2 },
    ],
  },
  {
    size: "A6",
    grammages: [
      { grammage: 70, weightPerReam_g: 543.9 },
      { grammage: 80, weightPerReam_g: 621.6 },
    ],
  },
  {
    size: "F4",
    grammages: [
      { grammage: 70, weightPerReam_g: 2687.09 },
      { grammage: 80, weightPerReam_g: 3070.96 },
    ],
  },
  {
    size: "F14",
    grammages: [
      { grammage: 70, weightPerReam_g: 2691.36 },
      { grammage: 80, weightPerReam_g: 3075.84 },
    ],
  },
  {
    size: "B4",
    grammages: [
      { grammage: 70, weightPerReam_g: 3088.75 },
      { grammage: 80, weightPerReam_g: 3530.0 },
    ],
  },
  {
    size: "B5",
    grammages: [
      { grammage: 70, weightPerReam_g: 1540.0 },
      { grammage: 80, weightPerReam_g: 1760.0 },
    ],
  },
  {
    size: "US Letter",
    grammages: [
      { grammage: 70, weightPerReam_g: 2111.2 },
      { grammage: 80, weightPerReam_g: 2412.8 },
    ],
  },
  {
    size: "Continuous 9x11",
    grammages: [
      { grammage: 50, weightPerReam_g: 1596.75 },
      { grammage: 60, weightPerReam_g: 1916.1 },
    ],
  },
];

const AddPurchases = () => {
  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const { inputAmountType } = AddEmissionContext();

  const stepTwoForm = useFormContext();

  const standardPaperSize: string | undefined =
    stepTwoForm.watch("standardPaperSize");
  const paperWeight: string | undefined = stepTwoForm.watch("paperWeight");
  const amountOfReam: string | undefined = stepTwoForm.watch("amountOfReam");

  const [paperWeightOption, setPaperWeightOption] = useState<MenuData[]>([]);
  const [weightPerReam, setWeightPerReam] = useState<number>(0);

  useEffect(() => {
    if (!standardPaperSize) {
      setPaperWeightOption([]);
      stepTwoForm.setValue("paperWeight", null, { shouldValidate: true }); // ✅ เคลียร์ค่าทิ้งป้องกันผีหลอก
      return;
    }

    // ✅ ใช้ .find() แทน .filter()[0] ป้องกัน App Crash
    const foundGroup = paperGrammagesGrouped.find(
      (e) => e.size === standardPaperSize,
    );

    if (foundGroup) {
      const optionTemp: MenuData[] = foundGroup.grammages.map((e) => ({
        nameEN: `${e.grammage}`,
        nameTH: `${e.grammage}`,
        value: `${e.grammage}`,
      }));

      setPaperWeightOption(optionTemp);

      // ตรวจสอบว่า paperWeight เดิม ยังคงมีให้เลือกในไซส์ใหม่หรือไม่
      const isCurrentWeightStillValid = optionTemp.some(
        (opt) => opt.value === paperWeight,
      );

      if (optionTemp.length === 1) {
        stepTwoForm.setValue("paperWeight", optionTemp[0].value, {
          shouldValidate: true,
        });
      } else if (!isCurrentWeightStillValid) {
        stepTwoForm.setValue("paperWeight", null, { shouldValidate: true }); // ✅ ล้างค่าเก่าที่ไม่มีในลิสต์ใหม่ทิ้ง
      }
    }
  }, [standardPaperSize]); // ไม่ใส่ paperWeight เป็น deps เพื่อไม่ให้เกิด loop

  // 2. จัดการดึงน้ำหนักต่อรีม (คำนวณกรัม)
  useEffect(() => {
    if (standardPaperSize && paperWeight) {
      const foundGroup = paperGrammagesGrouped.find(
        (e) => e.size === standardPaperSize,
      );
      const temp = foundGroup?.grammages.find(
        (g) => `${g.grammage}` === paperWeight,
      );

      if (temp) {
        setWeightPerReam(temp.weightPerReam_g);
      } else {
        setWeightPerReam(0); // ✅ กันบั๊กน้ำหนักผีสิง (ถ้าไม่ตรงให้เป็น 0 ไว้ก่อน)
      }
    } else {
      setWeightPerReam(0);
    }
  }, [standardPaperSize, paperWeight]);

  // 3. จัดการคำนวณ Amount โชว์ลงฟอร์ม
  useEffect(() => {
    if (inputAmountType === "special") {
      if (amountOfReam && weightPerReam > 0 && standardPaperSize) {
        const result: number = (Number(amountOfReam) * weightPerReam) / 1000;
        stepTwoForm.setValue(
          "amount",
          Number.isNaN(result) ? "" : result.toFixed(2), // ตรงนี้ .toFixed(2) หรือ (4) ตามที่เคยคุยกันก็ได้ครับ
          { shouldValidate: true },
        );
      } else {
        // ✅ ถ้าเคลียร์ช่องรีมทิ้ง ก็ต้องเคลียร์ช่อง Amount ด้วย
        stepTwoForm.setValue("amount", null, { shouldValidate: true });
      }
    }
  }, [amountOfReam, weightPerReam, standardPaperSize, inputAmountType]);

  return (
    <FormProvider {...stepTwoForm}>
      <Grid item xs={12} md={6}>
        <AmountInput
          label={t("Amount-of-Purchases")}
          info={t("Amount-of-Purchases-Description")}
          control={stepTwoForm.control}
          disabled={inputAmountType === "special" ? true : false}
        />
      </Grid>
      {inputAmountType === "special" && (
        <>
          <Grid item xs={12} md={6}>
            <CustomAutoComplete
              required
              name={"standardPaperSize"}
              control={stepTwoForm.control}
              rules={{
                required: t("This-field-is-required", {
                  param: t("Standard-Paper-Size"),
                }),
              }}
              label={t("Standard-Paper-Size")}
              info={t("Standard-Paper-Size-Description")}
              placeholder={t("Select-Param", {
                param: t("Standard-Paper-Size"),
              })}
              listMenuItem={paperGrammagesGrouped.map((e) => ({
                nameEN: e.size,
                nameTH: e.size,
                value: e.size,
              }))}
              defaultValue={undefined}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomAutoComplete
              required
              name={"paperWeight"}
              control={stepTwoForm.control}
              rules={{
                required: t("This-field-is-required", {
                  param: t("Paper-Weight-GSM"),
                }),
              }}
              label={t("Paper-Weight-GSM")}
              info={t("Paper-Weight-GSM-Description")}
              placeholder={t("Select-Param", {
                param: t("Paper-Weight-GSM"),
              })}
              listMenuItem={paperWeightOption}
              disabled={!standardPaperSize} // ✅ ล็อคไว้ถ้ายังไม่เลือกไซส์กระดาษ
              defaultValue={undefined}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumericField
              required
              size="small"
              name={"amountOfReam"}
              control={stepTwoForm.control}
              allowNegative={false}
              decimalScale={2} // 💡 เผื่อ User กรอกแบบ "ครึ่งรีม (0.5)" ถ้าอนุญาตให้พิมพ์ได้
              label={t("Amount-Of-Ream")}
              info={t("Amount-Of-Ream-Description")}
              placeholder={t("Amount-Of-Ream")}
              fullWidth
              disabled={!paperWeight} // ✅ ล็อคไว้ถ้ายังไม่เลือกแกรม
              InputProps={{
                endAdornment: (
                  <Typography variant="text1">{t("Ream")}</Typography>
                ),
              }}
            />
          </Grid>
        </>
      )}
    </FormProvider>
  );
};

export default AddPurchases;
